/**
 * ScheduleFromApprovalModal
 *
 * Allows agency staff / admins to schedule an APPROVED post directly from
 * the approval board by dragging it into the "Scheduled" column.
 *
 * Flow:
 *   1. User drags an APPROVED card to the Scheduled column
 *   2. This modal opens
 *   3. User picks calendar, date/time, and channels
 *   4. On submit → POST /api/Post/:postId/schedule-from-approval
 *   5. On success → refetch approval board, close modal, show toast
 *
 * Reuses:
 *   - SchedulePicker (date + time with timezone cascade)
 *   - useCalendars (team calendars)
 *   - useChannels (team channels)
 *   - useTimezone (timezone cascade per CLAUDE.md)
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Icon } from '@/components/ui/Icon';
import { SchedulePicker } from '@/components/composer/SchedulePicker';
import { useCalendars } from '@/hooks/useCalendar';
import { useChannels } from '@/hooks/useChannels';
import { useTimezone } from '@/contexts/TimezoneContext';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { approvalService } from '@/services/approval.service';
import { teamQueryKeys } from '@/lib/queryKeys';
import { invalidateReleaseState } from '@/lib/invalidateRelease';
import { ICONS, PLATFORM_ICONS } from '@/constants/icons';
import { cn } from '@/utils/cn';
import { Channel } from '@/services/channels.service';
import { getChannelDisplayName } from '@/utils/channelLabel';
import { getPlatformDisplayName } from '@/constants/platformLimits';
import { PendingApproval } from '@/types/approval';
import { classifySubmitWarning } from '@/utils/shootRecovery';

// ============================================================================
// TYPES
// ============================================================================

export interface ScheduleFromApprovalModalProps {
  /** The approval item being scheduled */
  item: PendingApproval;
  /** Whether the modal is open */
  isOpen: boolean;
  /** Called when modal is closed or dismissed */
  onClose: () => void;
  /** Called after successful scheduling */
  onSuccess: () => void;
}

// ============================================================================
// CHANNEL SELECTOR SUBCOMPONENT
// ============================================================================

interface ChannelSelectorProps {
  channels: Channel[];
  selectedIds: number[];
  onChange: (ids: number[]) => void;
  disabled?: boolean;
}

const ChannelSelector = ({
  channels,
  selectedIds,
  onChange,
  disabled = false,
}: ChannelSelectorProps) => {
  const { t } = useTranslation('common');

  const toggle = (id: number) => {
    if (selectedIds.includes(id)) {
      onChange(selectedIds.filter((c) => c !== id));
    } else {
      onChange([...selectedIds, id]);
    }
  };

  if (channels.length === 0) {
    return (
      <p className="text-sm text-gray-500 italic">
        {t('schedule_approval_no_channels')}
      </p>
    );
  }

  return (
    <div className="space-y-2 max-h-48 overflow-y-auto pr-1 -mr-1">
      {channels.map((ch) => {
        const isSelected = selectedIds.includes(ch.id);
        const platformIcon = PLATFORM_ICONS[ch.platform?.toLowerCase()] ?? ICONS.GLOBE;
        return (
          <label
            key={ch.id}
            className={cn(
              'flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 cursor-pointer transition-all',
              'min-h-[44px]', // accessible tap target
              isSelected
                ? 'border-primary-500 bg-primary-50 shadow-sm'
                : 'border-gray-200 bg-white hover:border-gray-300 hover:bg-gray-50',
              disabled && 'opacity-50 cursor-not-allowed'
            )}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => toggle(ch.id)}
              disabled={disabled}
              className="w-4 h-4 rounded focus:ring-primary-500 accent-primary-500 flex-shrink-0"
            />
            {/* Platform icon */}
            <span className={cn(
              'w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center text-sm',
              isSelected ? 'bg-primary-100' : 'bg-gray-100'
            )}>
              <Icon name={platformIcon} size="sm" color={isSelected ? 'text-primary-600' : 'text-gray-500'} ariaHidden />
            </span>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">
                {getChannelDisplayName(ch)}
              </p>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider font-medium">
                {getPlatformDisplayName(ch.platform.toLowerCase())}
              </p>
            </div>
            {isSelected && (
              <Icon name={ICONS.CHECK} size="sm" color="text-primary-600" ariaHidden />
            )}
          </label>
        );
      })}
    </div>
  );
};

// ============================================================================
// SECTION LABEL SUBCOMPONENT
// ============================================================================

interface SectionLabelProps {
  icon: string;
  label: string;
  subtitle?: string;
  badge?: string;
  required?: boolean;
  hasValue?: boolean;
}

const SectionLabel = ({ icon, label, subtitle, badge, required = false, hasValue = false }: SectionLabelProps) => (
  <div className="flex items-start justify-between mb-2">
    <div>
      <span className="flex items-center gap-1.5 text-sm font-semibold text-gray-800">
        <Icon name={icon} size="sm" color={hasValue ? 'text-primary-600' : 'text-gray-400'} ariaHidden />
        {label}
        {required && <span className="text-coral-500 ml-0.5">*</span>}
      </span>
      {subtitle && (
        <p className="text-xs text-gray-400 mt-0.5 ml-5">{subtitle}</p>
      )}
    </div>
    {badge !== undefined && (
      <span className={cn(
        'text-xs px-2 py-0.5 rounded-full font-medium',
        Number(badge) > 0
          ? 'bg-primary-100 text-primary-700'
          : 'bg-gray-100 text-gray-400'
      )}>
        {badge}
      </span>
    )}
  </div>
);

// ============================================================================
// MAIN MODAL COMPONENT
// ============================================================================

export const ScheduleFromApprovalModal = ({
  item,
  isOpen,
  onClose,
  onSuccess,
}: ScheduleFromApprovalModalProps) => {
  const { t } = useTranslation('common');
  const { t: tPosts } = useTranslation('posts');
  const queryClient = useQueryClient();
  const { teamId } = useEffectiveTeamId();
  const { formatDateTime, timezone } = useTimezone();

  // Form state
  const [selectedCalendarId, setSelectedCalendarId] = useState<number | null>(null);
  const [scheduledAt, setScheduledAt] = useState<Date | null>(null);
  const [selectedChannelIds, setSelectedChannelIds] = useState<number[]>([]);

  // Data fetching
  const { data: calendars = [], isLoading: calendarsLoading } = useCalendars();
  const { data: allChannels = [], isLoading: channelsLoading } = useChannels();

  const isLoading = calendarsLoading || channelsLoading;

  // Validation
  const canSubmit = useMemo(
    () =>
      selectedCalendarId !== null &&
      scheduledAt !== null &&
      selectedChannelIds.length > 0,
    [selectedCalendarId, scheduledAt, selectedChannelIds]
  );

  // Per-section validation hints (only show when user has interacted with prior steps)
  const missingCalendar = selectedCalendarId === null;
  const missingDatetime = scheduledAt === null;
  const missingChannels = selectedChannelIds.length === 0;

  // Mutation
  const scheduleMutation = useMutation({
    mutationFn: () => {
      if (!scheduledAt) throw new Error('No scheduled time');
      return approvalService.scheduleFromApproval(item.post.id, {
        calendarId: selectedCalendarId!,
        scheduledAt: scheduledAt.toISOString(),
        channelIds: selectedChannelIds,
        ...(item.variation ? { variationId: item.variation.id } : {}),
      });
    },
    onSuccess: () => {
      const scheduledPostId = Number(item.post.id);
      invalidateReleaseState(queryClient, {
        teamId,
        postId: Number.isFinite(scheduledPostId) ? scheduledPostId : undefined,
      });
      queryClient.invalidateQueries({
        queryKey: teamQueryKeys.calendars.all(teamId),
      });
      onSuccess();
    },
  });

  const handleSubmit = () => {
    if (!canSubmit) return;
    scheduleMutation.mutate();
  };

  const handleClose = () => {
    setSelectedCalendarId(null);
    setScheduledAt(null);
    setSelectedChannelIds([]);
    scheduleMutation.reset();
    onClose();
  };

  // Post preview data
  const captionExcerpt = item.post.content
    ? item.post.content.slice(0, 120) + (item.post.content.length > 120 ? '…' : '')
    : t('approval_no_content');

  const thumbnailUrl = item.post.mediaUrls?.[0] ?? null;

  // already_scheduled 409 -> formatted amber warning instead of the raw backend string
  const submitWarning = scheduleMutation.error
    ? classifySubmitWarning(scheduleMutation.error, tPosts, formatDateTime)
    : null;

  // Footer — sticky at bottom of modal
  const footer = (
    <div className="flex flex-col gap-3">
      {/* Error block above footer buttons */}
      {scheduleMutation.error && (
        <div
          className={`flex items-start gap-2 px-3 py-2.5 border rounded-xl ${
            submitWarning ? 'bg-amber-50 border-amber-200' : 'bg-coral-50 border-coral-200'
          }`}
        >
          <Icon
            name={ICONS.WARNING}
            size="sm"
            color={submitWarning ? 'text-amber-700' : 'text-coral-500'}
            ariaHidden
          />
          <p className={`text-sm flex-1 ${submitWarning ? 'text-amber-800' : 'text-coral-700'}`}>
            {submitWarning
              ? submitWarning.message
              : scheduleMutation.error instanceof Error
                ? scheduleMutation.error.message
                : t('schedule_approval_error')}
          </p>
        </div>
      )}

      <div className="flex gap-3">
        <Button
          variant="secondary"
          onClick={handleClose}
          disabled={scheduleMutation.isPending}
          className="flex-1 sm:flex-none"
        >
          {t('approval_cancel')}
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          loading={scheduleMutation.isPending}
          disabled={!canSubmit || isLoading}
          className="flex-1 sm:flex-none sm:ml-auto gap-1.5"
        >
          <Icon name={ICONS.CALENDAR} size="sm" ariaHidden />
          {t('schedule_approval_submit')}
        </Button>
      </div>
    </div>
  );

  return (
    <Modal
      open={isOpen}
      onOpenChange={(open) => !open && handleClose()}
      title={t('schedule_approval_modal_title')}
      size="md"
      footer={footer}
    >
      <div className="space-y-5">
        {/* Post preview card — header context */}
        <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-200">
          {/* Thumbnail */}
          <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-gray-200 shadow-sm ring-1 ring-gray-100">
            {thumbnailUrl ? (
              <img
                src={thumbnailUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center">
                <Icon name={ICONS.IMAGE} size="md" color="text-gray-400" ariaHidden />
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-gray-800 line-clamp-3 leading-relaxed">
              {captionExcerpt}
            </p>
          </div>
        </div>

        {/* ── Section 1: Calendar ── */}
        <div className="space-y-2">
          <SectionLabel
            icon={ICONS.CALENDAR}
            label={t('schedule_approval_calendar')}
            required
            hasValue={!missingCalendar}
          />
          {calendarsLoading ? (
            <div className="h-10 bg-gray-100 rounded-xl animate-pulse" />
          ) : (
            <select
              value={selectedCalendarId ?? ''}
              onChange={(e) => setSelectedCalendarId(Number(e.target.value) || null)}
              disabled={scheduleMutation.isPending}
              className={cn(
                'w-full px-3 py-2.5 text-sm border-2 rounded-xl transition-colors',
                'focus:outline-none focus:border-primary-500 focus:ring-2 focus:ring-primary-200',
                !missingCalendar
                  ? 'border-primary-400 bg-primary-50 text-gray-900'
                  : 'border-gray-200 bg-white text-gray-700',
                'disabled:opacity-50 disabled:cursor-not-allowed'
              )}
            >
              <option value="">
                {t('schedule_approval_select_calendar')}
              </option>
              {calendars.map((cal) => (
                <option key={cal.id} value={cal.id}>
                  {cal.name}
                  {cal.isDefault
                    ? ` (${t('schedule_approval_default_calendar')})`
                    : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* ── Section 2: Date & Time ── */}
        <div className="space-y-2">
          <SectionLabel
            icon={ICONS.CLOCK}
            label={t('schedule_approval_datetime')}
            subtitle={t('schedule_approval_timezone_hint', {
              defaultValue: 'Time shown in {{timezone}}',
              timezone: timezone || 'Europe/Berlin',
            })}
            required
            hasValue={!missingDatetime}
          />
          <SchedulePicker
            scheduledTime={scheduledAt}
            onScheduleChange={setScheduledAt}
            disabled={scheduleMutation.isPending}
            directMode
            compact
          />
          {scheduledAt && (
            <p className="text-xs text-primary-600 font-medium flex items-center gap-1">
              <Icon name={ICONS.CHECK} size="xs" ariaHidden />
              {formatDateTime(scheduledAt.toISOString())}
            </p>
          )}
        </div>

        {/* ── Section 3: Channels ── */}
        <div className="space-y-2">
          <SectionLabel
            icon={ICONS.GRID}
            label={t('schedule_approval_channels')}
            required
            hasValue={!missingChannels}
            badge={String(selectedChannelIds.length)}
          />
          {channelsLoading ? (
            <div className="h-24 bg-gray-100 rounded-xl animate-pulse" />
          ) : (
            <ChannelSelector
              channels={allChannels}
              selectedIds={selectedChannelIds}
              onChange={setSelectedChannelIds}
              disabled={scheduleMutation.isPending}
            />
          )}
        </div>
      </div>
    </Modal>
  );
};

export default ScheduleFromApprovalModal;
